import React from 'react'

import { compare } from 'er2_web_utils'

import * as actionTypes from '../constants/action_types'
import * as ids from '../constants/ids'
import GeoBarClip from '../components/geo_bar_clip'
import GeoBarIntersection from '../components/geo_bar_intersection'
import GeoBarUnion from '../components/geo_bar_union'
import GeoBarBuffer from '../components/geo_bar_buffer'
import GeoBarDissolve from '../components/geo_bar_dissolve'
import GeoBarZonalStats from '../components/geo_bar_zonal_stats'
import GeoBarReclassify from '../components/geo_bar_reclassify'
import GeoBarCalculator from '../components/geo_bar_calculator'
import GeoBarDigitize from '../components/geo_bar_digitize'

// Default function to return a position at the bottom of the GeoBar
export const getBottomPosition = props => 'bottom'
// Default function to return a position at the top of the GeoBar
export const getTopPosition = props => 'top'

export const homeComponent = {
    toolbar: {
        active: false,
        icon: { default: 'ms ms-catalog' },
        id: ids.GEO_BAR_HOME,
        name: 'GIS Catalog',
        position: getTopPosition,
        items: [],
    },
}

export const searchComponent = {
    helpActive: false,
    toolbar: {
        active: false,
        icon: { default: 'fa fa-search' },
        id: ids.GEO_BAR_SEARCH,
        name: 'Search for a Tool',
        position: getTopPosition,
        items: [],
    },
}

// Represents a menu item in the GeoBar.  Each menu item can have items of its
// own, e.g.
// - Home
//   - Geoprocessing
//     - Vector
//     - Raster
//   - Area Selection
export type GeoBarItemType = {
    // Actions local to each tool can be held here
    actions: object,
    // Place for handling local state associated with the tool in operation
    state: object,
    id: string,
    label: string,
    fullLabel: string,
    render: Function,
    toolbar: {
        id: string,
        name: string,
        icon: Function,
        active: boolean,
        isEnabled: Function,
        isVisible: Function,
        position: Function,
        items: Array<GeoBarItemType>,
    },
}

export type GeoBarType = {
    homeComponent: GeoBarItemType,
    search: {
        active: boolean,
        suggestions: Array,
        value: string,
    },
    searchComponent: GeoBarItemType,
    tool?: string,
    tools: Array<GeoBarItemType>,
}

export const tools = [
    {
        boundaryLayer: '',
        targetLayer: '',
        description: 'Clip a target layer to a boundary',
        icon: 'ms ms-crop',
        id: ids.GEO_BAR_CLIP,
        label: 'Clip',
        fullLabel: 'Geoprocessing > Vector > Clip',
        render: props => (<GeoBarClip {...props} />),
        toolbar: {},
    }, {
        layers: [],
        icon: 'ms ms-intersection',
        id: ids.GEO_BAR_INTERSECT,
        description: 'Intersect two or more layers together',
        label: 'Intersection',
        fullLabel: 'Geoprocessing > Vector > Intersection',
        render: props => (<GeoBarIntersection {...props} />),
        toolbar: {},
    }, {
        layers: [],
        icon: 'ms ms-union',
        id: ids.GEO_BAR_UNION,
        description: 'Union two or more layers together',
        label: 'Union',
        fullLabel: 'Geoprocessing > Vector > Union',
        render: props => (<GeoBarUnion {...props} />),
        toolbar: {},
    }, {
        distance: 0,
        layers: [],
        icon: 'ms ms-buffer',
        id: ids.GEO_BAR_BUFFER,
        description: 'Buffer one or more layers',
        label: 'Buffer',
        fullLabel: 'Geoprocessing > Vector > Buffer',
        render: props => (<GeoBarBuffer {...props} />),
        toolbar: {},
    }, {
        key: '',
        layers: [],
        icon: 'ms ms-merge',
        id: ids.GEO_BAR_DISSOLVE,
        description: 'Dissolve two or more layers together',
        label: 'Dissolve',
        fullLabel: 'Geoprocessing > Vector > Dissolve',
        render: props => (<GeoBarDissolve {...props} />),
        toolbar: {},
    }, {
        boundaryLayer: '',
        targetLayer: '',
        icon: 'fa fa-bar-chart',
        id: ids.GEO_BAR_ZONAL_STATS,
        description: 'Retrieve zonal statistics for a raster',
        label: 'Zonal Statistics',
        fullLabel: 'Geoprocessing > Raster > Zonal Statistics',
        render: props => (<GeoBarZonalStats {...props} />),
        toolbar: {},
    }, {
        classificationCount: 5,
        classificationType: ids.GEO_BAR_NATURAL_BREAKS,
        targetLayer: '',
        icon: 'ms ms-resample',
        id: ids.GEO_BAR_RECLASS,
        description: 'Reclassify the values of a raster',
        label: 'Reclassify',
        fullLabel: 'Geoprocessing > Raster > Reclassify',
        render: props => (<GeoBarReclassify {...props} />),
        toolbar: {},
    }, {
        expression: '',
        layers: [],
        icon: 'fa fa-calculator',
        id: ids.GEO_BAR_CALCULATOR,
        description: 'Perform an arithmetic operation on a set of one or more rasters',
        label: 'Calculator',
        fullLabel: 'Geoprocessing > Raster > Calculator',
        render: props => (<GeoBarCalculator {...props} />),
        toolbar: {},
    }, {
        drawnFeatureName: '',
        drawPromptRendered: true,
        drawToolActive: false,
        knownBoundaryType: '',
        knownBoundaryId: '',
        editingLayer: '',
        icon: 'ms ms-draw-polygon',
        id: ids.GEO_BAR_DIGITIZE,
        description: 'Draw or edit features of a layer',
        label: 'Digitize & Edit',
        fullLabel: 'Digitize & Edit',
        method: '',
        render: props => (<GeoBarDigitize {...props} />),
        snappingLayer: '',
        toolbar: {},
    },
]

export const initialState = {
    active: false,
    enabled: true,
    homeComponent,
    searchComponent,
    search: {
        active: true,
        suggestions: [],
        value: '',
    },
    tools,
}

export const isGeoBarActive = (state, active) => {
    if (!state.mapToolbar.panel.visible) {
        return false
    }
    const activeMapToolbarItem = state.mapToolbar.items.find(item => item.id === ids.MAP_TOOLBAR_USER_LAYERS)
    if (!activeMapToolbarItem || !activeMapToolbarItem.active) {
        return false
    }
    // If the map toolbar panel is visible and the user layer panel is visible,
    // return the provided visibility
    return active
}

export function getSuggestions(value, suggestions) {
    const inputValue = value.trim().toLowerCase()
    const tokens = inputValue.split(' ')
    const inputLength = inputValue.length
    let count = 0

    return inputLength === 0
        ? suggestions
        : suggestions.sort((a, b) => compare(a.label, b.label)).filter((suggestion) => {
            const lbl = suggestion.fullLabel.toLowerCase()
            let idx = 0
            const matches = tokens.every((tkn) => {
                const foundIdx = lbl.indexOf(tkn, idx)
                if (foundIdx < idx) {
                    // console.log(`Couldn't find ${tkn} at ${lbl}. foundIdx = ${foundIdx}, idx = ${idx}`)
                    return false
                }
                // console.log(`Found ${tkn} at ${lbl} = ${foundIdx}`)
                idx = foundIdx
                return true
            })


            const keep = count < 100 && matches
            if (keep) {
                count += 1
            }
            return keep
        })
}

export function toggleLayerSelected(layer, isSelected: boolean, layerIds: Array) {
    if (isSelected) {
        layerIds.push(layer.id)
        return layerIds.filter((lyr, pos) => layerIds.indexOf(lyr) === pos)
    }
    // Remove the layer from selected layers
    return layerIds.filter(lyrId => lyrId !== layer.id)
}

function isDrawToolActive(method, tool) {
    if ([ids.GEO_BAR_DIG_METHOD_KNOWN_BOUNDARY].includes(method)) {
        return false
    }
    return method.length > 0
}

// @flow
export function reducer(state: GeoBarType = initialState, action: any) {
    switch (action.type) {
    case actionTypes.CHANGE_INPUT: {
        if (action.id === ids.GEO_BAR_BUFFER_DISTANCE) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_BUFFER) {
                        return {
                            ...tool,
                            distance: parseInt(action.value, 10),
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_CALCULATOR_EXPRESSION) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_CALCULATOR) {
                        return {
                            ...tool,
                            expression: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIGITIZE_DRAWN_FEATURE) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            drawnFeature: action.value,
                            drawToolActive: false,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIGITIZE_DRAWN_FEATURE_NAME) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            drawnFeatureName: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIGITIZE_DRAW_PROMPT_RENDERED) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            drawPromptRendered: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIG_KNOWN_BOUNDARY) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            knownBoundaryId: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIG_KNOWN_BOUNDARY_TYPE) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            knownBoundaryType: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIGITIZE_METHOD) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        // Remove the drawn feature whenever the method is updated
                        const { drawnFeature, knownBoundary, ...newState } = tool
                        const drawToolActive = isDrawToolActive(action.value, tool)
                        const drawPromptRendered = !drawToolActive
                        return {
                            ...newState,
                            method: action.value,
                            drawToolActive,
                            drawPromptRendered,
                            knownBoundaryType: '',
                            knownBoundaryId: '',
                            knownBoundaries: [],
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DISSOLVE_KEY) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DISSOLVE) {
                        return {
                            ...tool,
                            key: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_RECLASS_TYPE) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_RECLASS) {
                        return {
                            ...tool,
                            classificationType: action.value,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_RECLASS_COUNT) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_RECLASS) {
                        return {
                            ...tool,
                            classificationCount: parseInt(action.value, 10),
                        }
                    }
                    return tool
                }),
            }
        }
        return state
    }

    case actionTypes.CLICK_GEO_BAR_ITEM:
        if (action.id === ids.GEO_BAR_SEARCH) {
            return {
                ...state,
                search: {
                    ...state.search,
                    active: !state.search.active,
                },
            }
        }
        return state

    case actionTypes.CLICK_HELP_INLINE: {
        if (action.id === ids.HELP_GEO_BAR_SEARCH_TOOL) {
            return {
                ...state,
                searchComponent: {
                    ...state.searchComponent,
                    helpActive: !state.searchComponent.helpActive,
                },
            }
        } else if (tools.find(tool => action.id === tool.id)) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === action.id) {
                        return {
                            ...tool,
                            helpActive: !tool.helpActive,
                        }
                    }
                    return tool
                }),
            }
        }
        return state
    }

    case actionTypes.GEO_BAR_FETCHED_OPERATION: {
        let newState = { ...state }
        if (action.id === ids.GEO_BAR_CLASSIFICATIONS) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_RECLASS) {
                        return {
                            ...tool,
                            classifications: action.result,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_DIGITIZE) {
            newState = {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        // Reset the draw tool
                        const { drawnFeature, knownBoundary, ...newTool } = tool
                        return {
                            ...newTool,
                            method: '',
                            drawnFeatureName: '',
                            knownBoundaryType: '',
                            knownBoundaryId: '',
                            knownBoundaries: [],
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_FETCH_KNOWN_BOUNDARIES) {
            newState = {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            knownBoundaries: action.result,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_FETCH_KNOWN_BOUNDARY) {
            newState = {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            drawnFeature: action.result,
                            knownBoundary: action.result,
                        }
                    }
                    return tool
                }),
            }
        }
        return {
            ...newState,
            tools: newState.tools.map((tool) => {
                if (tool.id === action.id) {
                    return {
                        ...tool,
                        result: action.result,
                        resultRendered: false,
                    }
                }
                return tool
            }),
        }
    }

    case actionTypes.FETCH_ERROR: {
        return {
            ...state,
            tools: state.tools.map((tool) => {
                if (tool.id === action.id) {
                    return {
                        ...tool,
                        result: action.result,
                        fetchError: action.error,
                    }
                }
                return tool
            }),
        }
    }

    case actionTypes.GEO_BAR_FETCHING_OPERATION: {
        if (action.id === ids.GEO_BAR_CLASSIFICATIONS) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_RECLASS) {
                        return {
                            ...tool,
                            isFetchingClassifications: action.isFetching,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_FETCH_KNOWN_BOUNDARIES) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            isFetchingKnownBoundaries: action.isFetching,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.id === ids.GEO_BAR_FETCH_KNOWN_BOUNDARY) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === ids.GEO_BAR_DIGITIZE) {
                        return {
                            ...tool,
                            isFetchingKnownBoundary: action.isFetching,
                        }
                    }
                    return tool
                }),
            }
        }
        return {
            ...state,
            tools: state.tools.map((tool) => {
                if (tool.id === action.id) {
                    return {
                        ...tool,
                        isFetching: action.isFetching,
                        fetchError: false,
                    }
                }
                return tool
            }),
        }
    }

    case actionTypes.GEO_BAR_FETCH_ERROR_RENDERED: {
        return {
            ...state,
            tools: state.tools.map((tool) => {
                if (tool.id === action.id) {
                    return {
                        ...tool,
                        fetchError: false,
                    }
                }
                return tool
            }),
        }
    }

    case actionTypes.GEO_BAR_RESULT_RENDERED: {
        return {
            ...state,
            tools: state.tools.map((tool) => {
                if (tool.id === action.id) {
                    return {
                        ...tool,
                        resultRendered: true,
                    }
                }
                return tool
            }),
        }
    }

    case actionTypes.GEO_BAR_SELECT_LAYER: {
        if ([ids.GEO_BAR_INTERSECT, ids.GEO_BAR_UNION, ids.GEO_BAR_BUFFER, ids.GEO_BAR_DISSOLVE].includes(action.tool.id)) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === action.tool.id) {
                        const layers = toggleLayerSelected(action.layer, action.isSelected, tool.layers)
                        return {
                            ...tool,
                            layers,
                        }
                    }
                    return tool
                }),
            }
        } else if ([ids.GEO_BAR_CLIP, ids.GEO_BAR_ZONAL_STATS].includes(action.tool.id)) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === action.tool.id) {
                        if (action.opts.kind === 'targetLayer') {
                            // Reset the boundary layer if the target layer is the same,
                            // since you cannot clip a layer to itself.
                            const boundaryLayer = action.layer.id === tool.boundaryLayer ? '' : tool.boundaryLayer
                            return {
                                ...tool,
                                boundaryLayer,
                                targetLayer: action.layer.id,
                            }
                        } else if (action.opts.kind === 'boundaryLayer') {
                            return {
                                ...tool,
                                boundaryLayer: action.layer.id,
                            }
                        }
                    }
                    return tool
                }),
            }
        } else if (action.tool.id === ids.GEO_BAR_RECLASS) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === action.tool.id) {
                        return {
                            ...tool,
                            targetLayer: action.layer.id,
                        }
                    }
                    return tool
                }),
            }
        } else if (action.tool.id === ids.GEO_BAR_DIGITIZE) {
            return {
                ...state,
                tools: state.tools.map((tool) => {
                    if (tool.id === action.tool.id && action.opts.kind === 'editingLayer') {
                        return {
                            ...tool,
                            editingLayer: action.layer.id,
                        }
                    } else if (tool.id === action.tool.id && action.opts.kind === 'snappingLayer') {
                        return {
                            ...tool,
                            snappingLayer: action.layer.id,
                        }
                    }
                    return tool
                }),
            }
        }
        return state
    }

    case actionTypes.POST_CLICK_MAP_TOOLBAR_ITEM: {
        return {
            ...state,
            active: isGeoBarActive(action.state, state.active),
        }
    }

    case actionTypes.GEO_BAR_CHANGE_SEARCH_VALUE: {
        if (typeof action.value === 'string') {
            // Sometimes the React Autosuggest component will emit a change event when a tool is selected,
            // passing an integer of the index of the tool in the list of suggestions. We don't care about
            // handling this here, since it's already handled with the GEO_BAR_SELECT_TOOL event
            return {
                ...state,
                search: {
                    ...state.search,
                    value: action.value,
                },
            }
        }
        return state
    }

    case actionTypes.GEO_BAR_SELECT_TOOL: {
        return {
            ...state,
            tool: action.tool.id,
            search: {
                ...state.search,
                active: false,
                value: action.tool.label,
            },
        }
    }

    case actionTypes.GEO_BAR_SUGGESTIONS_CLEAR_REQUESTED: {
        return {
            ...state,
            search: {
                ...state.search,
                suggestions: [],
            },
        }
    }

    case actionTypes.GEO_BAR_SUGGESTIONS_FETCH_REQUESTED: {
        return {
            ...state,
            search: {
                ...state.search,
                suggestions: getSuggestions(action.value ? action.value.value : '', state.tools),
            },
        }
    }


    case actionTypes.POST_CLICK_GEO_BAR_ITEM: {
        if (action.id === ids.GEO_BAR_HOME) {
            return {
                ...state,
                active: isGeoBarActive(action.state, !state.active),
            }
        }
        return state
    }

    default:
        return state
    }
}

export default reducer
